/* =============================================================================
   CORE / TABLE-QR — Token, Gast-URL und Schloss-Zustand (S243, Cut A)
   =============================================================================
   Pure Helfer für „Handy-Bestellung am Tisch". Kein DOM, kein Storage-Zugriff —
   alles injizierbar, damit Vitest die Logik ohne Browser prüfen kann.

   DIE WICHTIGSTE REGEL DIESER DATEI:
   `selfOrderState()` ist die CLIENT-Spiegelung des SQL-Helpers
   `occupancy_self_order_state(p_occupancy_id)` aus phase_176. Beide müssen
   dieselben fünf Zustände in derselben Reihenfolge liefern. Der Server bleibt
   die Wahrheit (Cut B ruft ihn beim Scan über `resolve_table_session`); die
   Client-Fassung existiert nur, damit die Belegungs-Kachel nicht für jede
   Tisch-Kachel einen RPC feuert.

   Wer eine Regel ändert, ändert BEIDE Stellen — sonst zeigt das Cockpit „offen",
   während der Gast am Tisch eine Sperr-Seite sieht (oder umgekehrt, was
   schlimmer ist: der Service glaubt gesperrt zu haben und der Gast bestellt).

   Zustände:
     open          — freigegeben
     locked        — Operator-Schalter aus ODER Gast-Kanal „Bestellen am Tisch"
                     (`settings.guestChannels.table`, S266/phase_196) aus
     overdue       — geplante Sitzdauer + `selfOrderOverdueGraceMin` abgelaufen
     reserved_soon — Reservierung auf einem der Tische beginnt in ≤ `slotDurationMin`
     ended         — Belegung existiert nicht (mehr) / ist beendet / gelöscht
============================================================================ */

/** Alphabet des Tokens: 32 Zeichen ohne `l`, `o`, `0`, `1`. Der Token steht als
 *  Fallback-URL im Klartext unter dem QR-Code und wird im Zweifel abgetippt —
 *  darum ohne die vier Zeichen, die sich paarweise verwechseln lassen. Spiegel
 *  von `generate_table_qr_token()` (phase_176). */
import { guestChannelEnabled } from './guest-channels.js';

export const QR_TOKEN_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';
export const QR_TOKEN_LENGTH = 24;

const QR_TOKEN_RE = new RegExp(`^[${QR_TOKEN_ALPHABET}]{${QR_TOKEN_LENGTH}}$`);

/** Sieht der String aus wie ein Tisch-Token? (Format-Prüfung, keine Existenz.) */
export function isValidQrToken(token) {
  return typeof token === 'string' && QR_TOKEN_RE.test(token);
}

/**
 * Gast-URL für einen Tisch-Token.
 *
 * Das Format steht seit Cut A fest, weil der QR gedruckt und aufgestellt wird,
 * bevor die Route existiert.
 *
 * @param {string} origin  z. B. 'https://gastro.yourgrowth365.de' (Slash am Ende egal)
 * @param {string} token   qr_token des Tisches
 */
export function tableQrUrl(origin, token) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/#tisch/${token || ''}`;
}

/**
 * S244-HF — welcher Origin gehört auf ein GEDRUCKTES Papier?
 *
 * Nicht der, auf dem das Cockpit gerade läuft. Ein aus dem Dev-Server gedruckter
 * Bogen trug `http://127.0.0.1:5500/#tisch/…`; auf Jonas' Handy kam „Server nicht
 * erreichbar". Der Aufsteller überlebt die Sitzung, in der er entstand — also
 * muss die URL aus der Konfiguration kommen, nicht aus dem Browser-Kontext.
 *
 * `location.origin` bleibt der Fallback für lokale Forks und Tests. Pure, damit
 * beide Wege prüfbar sind.
 *
 * @param {object} config          window.AppConfig (oder ein Ausschnitt davon)
 * @param {string} locationOrigin  window.location.origin
 */
export function resolveGuestOrigin(config, locationOrigin) {
  const configured = config && typeof config.guestOrigin === 'string'
    ? config.guestOrigin.trim() : '';
  if (configured) return configured.replace(/\/+$/, '');
  return String(locationOrigin || '').replace(/\/+$/, '');
}

/** Default-Settings dieses Features an EINER Stelle (Spiegel der SQL-Defaults). */
export function selfOrderDefaults() {
  return {
    selfOrderAutoEnable: true,
    selfOrderOverdueGraceMin: 15
  };
}

/** Ein Setting mit Default-Fallback lesen. `undefined` UND `null` fallen zurück —
 *  ein Tenant, dessen settings-JSON den Schlüssel noch nicht kennt (Live-Stand
 *  bei allen drei Tenants), soll den Default sehen, nicht `undefined`. */
export function selfOrderSetting(settings, key) {
  const s = settings || {};
  const d = selfOrderDefaults();
  const v = s[key];
  return (v === undefined || v === null) ? d[key] : v;
}

/** Tische, die eine Belegung hält — `tableId` UND `assignedTableIds`, wie
 *  getTableSharingState und die SQL-Fassung. */
export function occupancyTableIds(occ) {
  if (!occ) return [];
  const out = new Set();
  if (Array.isArray(occ.assignedTableIds)) occ.assignedTableIds.forEach(t => { if (t) out.add(t); });
  if (occ.tableId) out.add(occ.tableId);
  return Array.from(out);
}

/**
 * Beginnt eine Reservierung in den nächsten `withinMin` Minuten auf einem der
 * Tische? Lokale Wanduhr-Zeiten (`date` + `time`) werden gegen `nowMs`
 * verglichen — der Browser läuft in derselben Zeitzone wie das Restaurant,
 * deshalb reicht hier `new Date('YYYY-MM-DDTHH:MM')` (die SQL-Fassung braucht
 * dafür `AT TIME ZONE 'Europe/Berlin'`, weil die DB-Session in UTC läuft).
 */
export function reservationDueSoon(reservations, tableIds, opts = {}) {
  const list = Array.isArray(reservations) ? reservations : [];
  const ids = Array.isArray(tableIds) ? tableIds : [];
  if (!ids.length) return null;
  const nowMs = Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now();
  const withinMin = Number.isFinite(opts.withinMin) ? opts.withinMin : 90;
  const ownReservationId = opts.ownReservationId || null;
  const untilMs = nowMs + withinMin * 60 * 1000;

  for (const r of list) {
    if (!r || r.deletedAt) continue;
    if (r.status !== 'confirmed') continue;
    // Schon eingecheckt = die Gäste sitzen bereits; das ist kein kommender
    // Konflikt, sondern der aktuelle Zustand.
    if (r.arrivedAt) continue;
    // Tisch wurde vorzeitig freigegeben (S134) → hält den Tisch nicht mehr.
    if (r.tableReleasedAt) continue;
    // Die EIGENE Reservierung sperrt ihre eigene Belegung nicht.
    if (ownReservationId && r.id === ownReservationId) continue;
    const tables = Array.isArray(r.assignedTables) ? r.assignedTables : [];
    if (!tables.some(t => ids.includes(t))) continue;
    if (!r.date || !r.time) continue;
    const startMs = new Date(`${r.date}T${String(r.time).slice(0, 5)}`).getTime();
    if (!Number.isFinite(startMs)) continue;
    if (startMs >= nowMs && startMs <= untilMs) return r;
  }
  return null;
}

/**
 * Der Schloss-Zustand einer Belegung. Spiegel von
 * `occupancy_self_order_state()` — Reihenfolge der Prüfungen inklusive.
 *
 * Warum der manuelle Schalter VOR den Auto-Gründen kommt: wenn jemand bewusst
 * gesperrt hat, ist das der Grund, den die Kachel nennen muss. „überfällig"
 * würde suggerieren, dass sich das Problem von selbst löst.
 *
 * @param {object}  args.occupancy    gemappte Belegung
 * @param {object}  args.settings     restaurant.settings
 * @param {Array}   [args.reservations]
 * @param {number}  [args.nowMs]
 * @returns {'open'|'locked'|'overdue'|'reserved_soon'|'ended'}
 */
export function selfOrderState({ occupancy, settings, reservations, nowMs } = {}) {
  const occ = occupancy;
  if (!occ || occ.deletedAt || occ.status !== 'active') return 'ended';

  const s = settings || {};
  // S266: Gast-Kanal „Bestellen am Tisch" statt Master-Schalter. Fehlender
  // Schlüssel = an (Spiegel von gg_guest_channel_enabled in phase_196).
  if (!guestChannelEnabled(s, 'table')) return 'locked';

  if (occ.selfOrderEnabled === false) return 'locked';

  const ref = Number.isFinite(nowMs) ? nowMs : Date.now();
  const startMs = occ.startedAt ? new Date(occ.startedAt).getTime() : NaN;
  if (Number.isFinite(startMs)) {
    const grace = Math.max(0, Number(selfOrderSetting(s, 'selfOrderOverdueGraceMin')) || 0);
    const limitMs = startMs + ((Number(occ.durationMin) || 90) + grace) * 60 * 1000;
    if (ref > limitMs) return 'overdue';
  }

  const slotMin = Math.max(0, Number(s.slotDurationMin) || 90);
  const due = reservationDueSoon(reservations, occupancyTableIds(occ), {
    nowMs: ref, withinMin: slotMin, ownReservationId: occ.reservationId || null
  });
  if (due) return 'reserved_soon';

  return 'open';
}

/** Darstellung eines Zustands: Symbol + Klartext für Kachel und Modal.
 *  EINE Quelle, damit Kachel-Badge und Modal-Zeile nie auseinanderlaufen. */
const STATE_VIEW = Object.freeze({
  open:          { icon: '🔓', label: 'Handy-Bestellung frei',              short: 'frei' },
  locked:        { icon: '🔒', label: 'Handy-Bestellung gesperrt',          short: 'gesperrt' },
  overdue:       { icon: '🔒', label: 'Gesperrt — Belegung überzogen',      short: 'überzogen' },
  reserved_soon: { icon: '🔒', label: 'Gesperrt — Reservierung steht an',   short: 'Reservierung' },
  ended:         { icon: '🔒', label: 'Gesperrt — Belegung beendet',        short: 'beendet' }
});

export function selfOrderView(state) {
  return STATE_VIEW[state] || STATE_VIEW.ended;
}

/** Kann der Gast in diesem Zustand bestellen? (Cut B: die einzige Frage, die
 *  `resolve_table_session` am Ende beantwortet.) */
export function selfOrderIsOpen(state) {
  return state === 'open';
}
